export default {
  app: {
    title: 'Analytics',
    description: 'Track and analyze your content performance',
  },
  header: {
    title: 'Your',
    titleAccent: 'analytics',
  },
  common: {
    title: 'Analytics',
    description: 'Track performance and insights',
    overview: 'Overview',
    percentage: 'Percentage',
    average: 'Average',
    vsPreviousPeriod: 'vs previous period',
    noData: 'No data available for the selected period',
    loading: 'Loading analytics data...',
    error: 'Failed to load analytics data',
    none: 'None',
    loadMore: 'Load more',
    analytics: 'Analytics',
  },
  tabs: {
    coverage: 'Coverage',
    topAssets: 'Top Content',
    geography: 'Geography',
    trafficSources: 'Traffic Sources',
    videoHeatmap: 'Heatmap',
    viewsOrigin: 'Plays Origin',
    reviews: 'Reviews',
    users: 'Users',
    viewers: 'Audience',
    sales: 'Sales',
    technical: 'Technical',
    audience: 'Audience',
    engagement: 'Engagement',
    contentAnalysis: 'Content Analysis',
    domains: 'Domains',
  },
  // A phone's picker for the tabs above, which do not fit there.
  viewPicker: {
    label: 'Statistics views',
    open: '{view}: change view',
  },
  coverage: {
    title: 'Coverage',
    description: 'Overview of your content performance and audience engagement',
    empty: {
      title: 'Your stats will appear here',
      description:
        "Here's what your dashboard will look like. Publish and share a video to bring it to life.",
      cta: 'Import a video',
    },
  },
  errors: {
    loadFailed: 'Failed to load coverage stats',
    loadAssetsFailed: 'An error occurred while loading assets',
    loadTrafficFailed: 'An error occurred while loading traffic sources',
  },
  period: {
    today: 'Today',
    yesterday: 'Yesterday',
    last7Days: 'Last 7 Days',
    last30Days: 'Last 30 Days',
    thisMonth: 'This Month',
    lastMonth: 'Last Month',
    thisYear: 'This Year',
    custom: 'Custom Range',
  },
  dateRange: 'Date Range',
  startDate: 'Start Date',
  endDate: 'End Date',
  quickShortcuts: 'Quick Shortcuts',
  shortcuts: {
    today: 'Today',
    yesterday: 'Yesterday',
    last7Days: 'Last 7 days',
    last30Days: 'Last 30 days',
    thisMonth: 'This month',
    lastMonth: 'Last month',
    thisYear: 'This year',
    lastYear: 'Last year',
  },
  metrics: {
    impressions: {
      label: 'Impressions',
      description: 'Number of times your content was displayed to users',
    },
    views: {
      label: 'Plays',
      description: 'Number of times your content was actually played',
    },
    uniqueViewers: {
      label: 'Unique Audience',
      description: 'Number of distinct users who played your content',
    },
    avgViewDuration: {
      label: 'Avg. Play Duration',
      description: 'Average time spent on your content per session',
    },
    totalWatchTime: {
      label: 'Total Play Time',
      description: 'Total accumulated time spent on all your content',
    },
    clickThroughRate: {
      label: 'Click-through Rate',
      description: 'Percentage of impressions that resulted in plays (Plays / Impressions)',
    },
    avgCompletionRate: {
      label: 'Avg. Completion Rate',
      description: 'Average percentage of content played before users stop',
    },
    assetsCount: {
      label: 'Media',
      description: 'Number of media files included in these statistics',
    },
    growth: 'Growth',
    sessions: 'Sessions',
    bounceRate: 'Bounce Rate',
    pagesPerSession: 'Pages per Session',
    newUsersPercentage: 'New Users',
  },
  granularity: {
    hour: 'Hour',
    day: 'Day',
    week: 'Week',
    month: 'Month',
  },
  charts: {
    timeSeries: 'Plays & Impressions',
    sessionsImpressions: {
      title: 'Plays & Impressions',
    },
    deviceType: {
      title: 'Device Distribution',
    },
    uniqueViewers: {
      title: 'Unique Audience Over Time',
    },
    watchTime: {
      title: 'Play Time Over Time',
      yAxisLabel: 'Duration',
    },
    uniqueViewersOverTime: 'Unique Audience',
    watchTimeOverTime: 'Play Time',
    deviceDistribution: 'Device Distribution',
    worldHeatmap: 'Geographic Distribution',
    topAssets: 'Top Assets',
    topCountries: 'Top Countries',
    topReferrers: 'Top Referrers',
    topTags: 'Top Tags',
  },
  origin: {
    worldHeatmap: {
      title: 'Plays by Country',
    },
  },
  devices: {
    desktop: 'Desktop',
    tablet: 'Tablet',
    mobile: 'Mobile',
    smartTv: 'Smart TV',
    gameConsole: 'Game Console',
    bot: 'Bot',
    unknown: 'Unknown',
  },
  contentTypes: {
    video: 'Video',
    audio: 'Audio',
  },
  geography: {
    title: 'Geographic Analytics',
    description: 'Discover where your audience is located and track geographic trends',
    topCountries: 'Top Countries',
    views: 'plays',
    regionalDistribution: 'Regional Distribution',
    continentalDistribution: 'Continental Distribution',
    topCities: 'Top Cities',
    totalCountries: 'Total Countries',
    topCountry: 'Top Country',
    topContinent: 'Top Continent',
    topRegion: 'Top Region',
    avgViewsPerCountry: 'Avg. Plays per Country',
    country: 'Country',
    percentage: 'Share',
    growth: 'Growth',
    viewAll: 'View All',
    totalCountriesDesc: 'Number of countries playing your content',
    topCountryDesc: 'Country with most plays',
    topContinentDesc: 'Continent with most plays',
    ofTotalViews: 'of total plays',
    avgViewsPerCountryDesc: 'Average plays from each country',
    uniqueViewers: 'Unique Audience',
    ctr: 'CTR',
    avgDuration: 'Avg Duration',
    noData: 'No geographic data',
    noDataDesc: 'Geographic data will appear once your content receives plays',
    regions: 'regions',
  },
  trafficSources: {
    title: 'Traffic Sources',
    description: 'Domains and pages where your content is embedded and played',
    trafficDistribution: 'Traffic Distribution',
    topSources: 'Top Sources',
    allReferrers: 'All Referrers',
    views: 'Plays',
    impressions: 'Impressions',
    viewers: 'Unique Audience',
    direct: 'Direct Traffic',
    directDesc: 'Visitors who typed your URL directly',
    searchEngines: 'Search Engines',
    socialMedia: 'Social Media',
    otherWebsites: 'Other Websites',
    referralDesc: 'External websites and blogs',
    ofTotal: 'of total',
    directTraffic: 'Direct',
    totalReferrers: 'Total Referrers',
    totalReferrersDesc: 'Number of different traffic sources',
    topReferrer: 'Top Referrer',
    topReferrerDesc: 'Most traffic from this source',
    directTrafficDesc: 'Percentage of direct visitors',
    socialTraffic: 'Social Traffic',
    socialTrafficDesc: 'Traffic from social networks',
    source: 'Source',
    ctr: 'CTR',
    avgDuration: 'Avg Duration',
    completion: 'Completion',
    export: 'Export',
    social: 'Social',
    search: 'Search',
    sortByViews: 'Sort by Plays',
    sortByImpressions: 'Sort by Impressions',
    sortByCTR: 'Sort by CTR',
    sortByViewers: 'Sort by Audience',
    searchReferrers: 'Search referrers...',
    referrerDomain: 'Referrer Domain',
    uniqueViewers: 'Unique Audience',
    trafficShare: 'Traffic Share',
    campaign: 'Campaign',
    utmDescription: 'Campaign performance tracked via UTM parameters',
    none: 'None',
    noData: 'No traffic data',
    noDataDesc: 'Traffic source data will appear once your content receives plays',
  },
  filters: {
    title: 'Filters',
    subtitle: 'Refine your analytics data with custom filters',
    apply: 'Apply Filters',
    clear: 'Clear',
    activeCount: '{count} active filter(s)',
    noActiveFilters: 'No active filters',
    dateRange: 'Date Range',
    contentTypes: 'Content type',
    devices: 'Devices',
    devicesHint: 'Filter by device type (desktop, mobile, tablet, etc.)',
    countries: 'Countries',
    countriesHint: 'Filter by geographic location',
    referrers: 'Referrers',
    tags: 'Tags',
    tagsHint: 'Filter by content tags',
    workspaces: 'Workspaces',
  },
  reviews: {
    title: 'Reviews',
    description: 'Feedback and ratings from your audience',
    basedOn: 'Based on {count} reviews',
    sortBy: 'Sort by',
    allRatings: 'All ratings',
    all: 'All',
    export: 'Export',
    comments: 'Comments',
    recentReviews: 'Recent Reviews',
    helpful: 'Helpful',
    reply: 'Reply',
    noReviews: 'No reviews yet',
    noReviewsDesc: 'Reviews will appear here once your audience starts rating your content',
    noComment: 'No comment provided',
    mostRecent: 'Most recent',
    byRating: 'By rating',
    oldest: 'Oldest',
    ratingAsc: 'Rating ascending',
    ratingDesc: 'Rating descending',
    assetsReviewed: 'Assets Reviewed',
    avgReviewsPerAsset: '{avg} reviews per asset',
    avgRating: 'Average Rating',
    avgRatingDesc: 'Average rating across all reviews',
    totalReviews: 'Total Reviews',
    totalReviewsDesc: 'Total number of reviews received',
    assetsReviewedDesc: 'Number of unique assets with reviews',
    reviewsPerAsset: 'Reviews per Asset',
    reviewsPerAssetDesc: 'Average number of reviews per asset',
    satisfaction: 'Satisfaction',
    satisfactionDesc: 'Overall satisfaction score',
    ratingBreakdown: 'Rating Breakdown',
    ratingDistribution: 'Rating Distribution',
    insights: 'Insights',
    positive: 'Positive',
    neutral: 'Neutral',
    negative: 'Negative',
    positiveReviews: 'Positive reviews',
    neutralReviews: 'Neutral reviews',
    negativeReviews: 'Negative reviews',
    positiveReviewsDesc: 'Share of reviews rated 4 or 5 stars',
    neutralReviewsDesc: 'Share of reviews rated 3 stars',
    negativeReviewsDesc: 'Share of reviews rated 1 or 2 stars',
    responseRate: 'Response rate',
    reviews: 'reviews',
    anonymous: 'Anonymous',
    contact: 'Contact',
    viewAsset: 'View Asset',
    loadMore: 'Load More',
    loadingReviews: 'Loading reviews...',
    filtersIgnored: 'A rating carries no device, country or referrer: those filters do not apply to reviews.',
    notForCollections:
      'Ratings are given to a content item, not to a collection. Open one of its items to read its reviews.',
  },
  domains: {
    title: 'Embedding domains',
    description: 'Websites where your content is embedded, with their performance',
    noData: 'No domains for the selected period',
    stats: {
      totalDomains: 'Domains',
      totalDomainsDesc: 'Number of distinct domains embedding your content',
      totalSessions: 'Sessions',
      totalSessionsDesc: 'Playback sessions via embed in the period',
      totalViews: 'Plays',
      totalViewsDesc: 'Cumulative plays via embed',
      totalWatchTime: 'Play time',
      totalWatchTimeDesc: 'Total play time via embed',
    },
    table: {
      title: 'Domain ranking',
      domain: 'Domain',
      sessions: 'Sessions',
      views: 'Plays',
      uniqueViewers: 'Unique audience',
      urlCount: 'URLs',
      totalWatchTime: 'Total play time',
      avgWatchTime: 'Avg. duration',
      avgCompletion: 'Completion',
      firstViewedAt: 'First played',
      lastViewedAt: 'Last played',
      url: 'URL',
      actions: 'Actions',
      viewMore: 'View more',
      assetCount: 'Media',
      viewMedia: 'View media',
    },
    tooltips: {
      domain: 'Domain name of the website hosting the embedded player (e.g. blog.acme.com).',
      url: 'Full URL where your content is embedded (scheme + domain + path).',
      sessions: 'Total number of playback sessions started, including those ending in less than 10 seconds.',
      views: 'Number of sessions lasting more than 10 seconds. A "play" is a meaningful playback, unlike a session which can be an immediate bounce.',
      uniqueViewers: 'Number of distinct users who started at least one session. Computed from a viewer ID generated by the player.',
      urlCount: 'Number of distinct URLs (paths) on this domain where one of your content items is embedded.',
      totalWatchTime: 'Cumulative play time across all sessions for this domain.',
      avgWatchTime: 'Average playback session duration, including bounces.',
      avgCompletion: 'Average percentage of the content actually played per session. Green ≥ 75%, orange 40-74%, red < 40%.',
      firstViewedAt: 'Date of the very first session recorded on this domain — when the embed started.',
      lastViewedAt: 'Date of the most recent session — useful to spot embeds that have become inactive.',
      assetCount: 'Number of distinct media (video / audio) embedded on this URL.',
    },
    detail: {
      back: 'Back to domains',
      description: 'URLs and performance breakdown for this domain',
      visit: 'Visit site',
      unknownTitle: 'Unknown domain',
      notFound: 'No data for this domain',
      stats: {
        totalUrls: 'URLs',
        totalUrlsDesc: 'Number of distinct URLs on this domain embedding your content',
      },
      urlsTable: {
        title: 'URLs on this domain',
        url: 'URL',
      },
    },
    dialog: {
      title: 'Media embedded on this URL',
      assetName: 'Media name',
      noAssets: 'No media found for this URL',
      mediaCount: '{count} media | {count} media',
    },
  },
  topAssets: {
    title: 'Top Content',
    description: 'Track the performance of your best performing content',
    sortBy: 'Sort by',
    show: 'Show',
    days: 'days',
    allTime: 'All Time',
    assetName: 'Asset Name',
    views: 'Plays',
    impressions: 'Impressions',
    ctr: 'CTR',
    uniqueViewers: 'Unique Audience',
    avgDuration: 'Avg Duration',
    totalDuration: 'Total Duration',
    completion: 'Completion',
    trend: 'Trend',
    viewDetails: 'View Details',
    viewHeatmap: 'Heatmap',
    showMore: 'Show more',
    fullRanking: 'Full Ranking',
    noAssets: 'No assets found',
    noAssetsDesc: 'Upload assets to start tracking their performance',
    unavailableAsset: 'Unavailable content',
    metrics: {
      views: 'Plays',
      impressions: 'Impressions',
      uniqueViewers: 'Unique Audience',
      avgDuration: 'Average Duration',
      totalDuration: 'Total Duration',
      completion: 'Completion Rate',
      ctr: 'Click-through Rate',
    },
    limits: {
      top10: 'Top 10',
      top20: 'Top 20',
      top50: 'Top 50',
      top100: 'Top 100',
      all: 'All',
    },
  },
  export: {
    button: 'Export',
    excel: 'Excel (.xlsx)',
    error: 'The Excel file could not be produced. Try again in a moment.',
  },
  videoHeatmap: {
    title: 'Engagement Heatmap',
    description: 'Analyze audience engagement patterns throughout your content',
    selectVideo: 'Select content',
    searchPlaceholder: 'Search for content...',
    noVideosFound: 'No content found',
    timeRange: 'Time range',
    export: 'Export Data',
    totalViews: 'Total Plays',
    totalSessions: 'Total Sessions',
    avgCompletion: 'Avg. Completion',
    avgWatchTime: 'Avg Play Time',
    avgDuration: 'avg',
    videoDuration: 'Duration',
    peakTime: 'Peak Engagement',
    totalSessionsDesc: 'Total number of playback sessions for this content',
    avgCompletionDesc: 'Average share of the content played per session',
    videoDurationDesc: 'Total length of the content',
    peakTimeDesc: 'Point in the content with the highest engagement',
    dropOffRate: 'Drop-off Rate',
    engagementHeatmap: 'Engagement Heatmap',
    viewerEngagement: 'Audience Engagement',
    viewDensity: 'Play Density',
    absoluteViews: 'Absolute Plays',
    peakEngagement: 'Peak',
    highEngagement: 'High',
    mediumEngagement: 'Medium',
    lowEngagement: 'Low',
    basedOn: 'Based on {count} plays',
    recentSessions: 'Recent Sessions',
    sessionId: 'Session ID',
    user: 'User',
    device: 'Device',
    country: 'Country',
    watchPattern: 'Play Pattern',
    watchTime: 'Play Time',
    completion: 'Completion',
    engagement: 'Engagement',
    interactions: 'Interactions',
    seeks: 'Seeks',
    pauses: 'Pauses',
    date: 'Date',
    viewers: 'Audience',
    noVideoSelected: 'No content selected',
    selectVideoPrompt: 'Select content to view its engagement heatmap',
    last7Days: 'Last 7 days',
    last30Days: 'Last 30 days',
    last90Days: 'Last 90 days',
    allTime: 'All time',
    regions: 'regions',
    analyzingVideo: 'Analyzing content',
    changeVideo: 'Change',
    viewAsset: 'View content',
    noStatsTitle: 'No statistics for this content',
    noStatsDescription: 'This content hasn\'t been played over the selected period yet. Statistics will appear as soon as it gets plays.',
    noHeatmap: 'No session of this content over the selected period carries a heatmap.',
    sessionsWithoutHeatmap:
      '{count} session recorded by the player carries no heatmap and is not charted. | {count} sessions recorded by the player carry no heatmap and are not charted.',
    otherResolutions:
      '{count} session recorded at another resolution is not charted. | {count} sessions recorded at another resolution are not charted.',
    watchedBy: 'Sessions that played it',
  },
  users: {
    title: 'User Statistics',
    description: 'Monitor user activity, engagement and behavior patterns',
  },
  sales: {
    title: 'Sales Analytics',
    description: 'Track revenue, transactions and monetization performance',
  },
  technical: {
    title: 'Technical Analytics',
    description: 'Monitor system performance, resource usage and infrastructure metrics',
  },
  worldHeatmap: {
    watchTime: 'Play Time',
    hours: 'hours',
  },
  viewers: {
    title: 'Audience Statistics',
    description: 'Analyze authenticated audience engagement and behavior patterns',
    filterByAsset: 'Filter by asset',
    allAssets: 'All assets',
    viewersList: 'Audience List',

    // Summary stats
    totalViewers: 'Total Audience',
    totalViewersDesc: 'Unique authenticated users who played content',
    totalSessions: 'Total Sessions',
    totalSessionsDesc: 'Total playback sessions from authenticated users',
    avgWatchTime: 'Avg. Play Time',
    avgWatchTimeDesc: 'Average time spent per session',
    avgCompletion: 'Avg. Completion',
    avgCompletionDesc: 'Average content completion percentage',
    newViewers: 'New Audience',
    newViewersDesc: 'Audience with first visit in selected period',
    recurringViewers: 'Recurring Audience',
    recurringViewersDesc: 'Audience that returned for multiple sessions',
    engagementScore: 'Engagement Score',
    engagementScoreDesc: 'Combined score based on play time and completion',

    // Table columns
    email: 'Email',
    sessions: 'Sessions',
    assetsViewed: 'Assets Played',
    totalWatchTime: 'Total Play Time',
    completion: 'Completion',
    firstVisit: 'First Visit',
    lastVisit: 'Last Visit',

    // Modal
    recentSessions: 'Recent Sessions',
    asset: 'Asset',
    date: 'Date',
    watchTime: 'Play Time',
    device: 'Device',
    location: 'Location',
    unknownAsset: 'Unknown asset',
    deletedAccount: 'Deleted account',
  },
}
